/**
 * Constants for Accounting module
 */

// Transaction types
export const TRANSACTION_TYPES = {
    ORDER: 'order',
    ORDER_ITEM: 'order_item',
    SERVICE_ORDER: 'service_order',
    REFUND: 'refund',
    PAYOUT: 'payout'
}

export const TRANSACTION_TYPE_LABELS = {
    [TRANSACTION_TYPES.ORDER]: 'Order',
    [TRANSACTION_TYPES.ORDER_ITEM]: 'Order',
    [TRANSACTION_TYPES.SERVICE_ORDER]: 'Service Order',
    [TRANSACTION_TYPES.REFUND]: 'Refund',
    [TRANSACTION_TYPES.PAYOUT]: 'Payout'
}

export const TRANSACTION_TYPE_COLORS = {
    [TRANSACTION_TYPES.ORDER]: 'success',
    [TRANSACTION_TYPES.ORDER_ITEM]: 'success',
    [TRANSACTION_TYPES.SERVICE_ORDER]: 'info',
    [TRANSACTION_TYPES.REFUND]: 'danger',
    [TRANSACTION_TYPES.PAYOUT]: 'primary'
}
export const TRANSACTION_TYPE_ICON = {
    [TRANSACTION_TYPES.ORDER]: 'cipCircleCheck',
    [TRANSACTION_TYPES.SERVICE_ORDER]: 'cipInfo',
    [TRANSACTION_TYPES.REFUND]: 'cipReload',
    [TRANSACTION_TYPES.PAYOUT]: 'cipWallet02'
}

export const TRANSACTION_TYPE_OPTIONS = Object.entries(TRANSACTION_TYPE_LABELS).map(([value, label]) => ({
    value,
    label
}))

// Stakeholder types (UPPERCASE — matches backend response)
export const STAKEHOLDER_TYPES = {
    PHOTO_SERVICE: 'PHOTO_SERVICE',
    PICA: 'PICA',
    ORGANIZER: 'ORGANIZER'
}

export const STAKEHOLDER_TYPE_LABELS = {
    [STAKEHOLDER_TYPES.PHOTO_SERVICE]: 'Photo Service',
    [STAKEHOLDER_TYPES.PICA]: 'Pica',
    [STAKEHOLDER_TYPES.ORGANIZER]: 'Organizer'
}

export const STAKEHOLDER_TYPE_COLORS = {
    [STAKEHOLDER_TYPES.PHOTO_SERVICE]: 'info',
    [STAKEHOLDER_TYPES.PICA]: 'primary',
    [STAKEHOLDER_TYPES.ORGANIZER]: 'success'
}

export const STAKEHOLDER_TYPE_OPTIONS = Object.entries(STAKEHOLDER_TYPE_LABELS).map(([value, label]) => ({
    value,
    label
}))

// Movement types (for CM/PS movements channel)
export const MOVEMENT_TYPES = {
    ORDER_ITEM: 'order_item',
    REFUND: 'refund'
}

export const MOVEMENT_TYPE_LABELS = {
    [MOVEMENT_TYPES.ORDER_ITEM]: 'Order',
    [MOVEMENT_TYPES.REFUND]: 'Refund'
}

export const MOVEMENT_TYPE_COLORS = {
    [MOVEMENT_TYPES.ORDER_ITEM]: 'success',
    [MOVEMENT_TYPES.REFUND]: 'danger'
}

export const MOVEMENT_TYPE_ICON = {
    [MOVEMENT_TYPES.ORDER_ITEM]: 'cipCircleCheck',
    [MOVEMENT_TYPES.REFUND]: 'cipReload'
}

// Entry types
export const ENTRY_TYPES = {
    ORDER_REVENUE: 'order_revenue',
    ORDER_FEE: 'order_fee',
    ORDER_COST: 'order_cost',
    REFUND: 'refund',
    DISPUTE_FEE: 'dispute_fee',
    SAAS_REVENUE: 'saas_revenue',
    PAYOUT: 'payout'
}

export const ENTRY_TYPE_LABELS = {
    [ENTRY_TYPES.ORDER_REVENUE]: 'Revenue',
    [ENTRY_TYPES.ORDER_FEE]: 'Fee',
    [ENTRY_TYPES.ORDER_COST]: 'Cost',
    [ENTRY_TYPES.REFUND]: 'Refund',
    [ENTRY_TYPES.DISPUTE_FEE]: 'Dispute Fee',
    [ENTRY_TYPES.SAAS_REVENUE]: 'SaaS Revenue',
    [ENTRY_TYPES.PAYOUT]: 'Payout'
}

// Source types
export const SOURCE_TYPES = {
    ORDER_ITEM: 'order_item',
    SERVICE_ORDER_ITEM: 'service_order_item',
    REFUND: 'refund',
    PAYOUT: 'payout'
}

export const SOURCE_TYPE_LABELS = {
    [SOURCE_TYPES.ORDER_ITEM]: 'Order Item',
    [SOURCE_TYPES.SERVICE_ORDER_ITEM]: 'Service Order Item',
    [SOURCE_TYPES.REFUND]: 'Refund',
    [SOURCE_TYPES.PAYOUT]: 'Payout'
}

// Refund component types
export const REFUND_COMPONENT_TYPES = {
    PHOTOGRAPHER_FEE: 'photographer_fee',
    PICA_FEE: 'pica_fee',
    ADDITIONAL_FEE: 'additional_fee',
    VAT: 'vat',
    STRIPE_FEE: 'stripe_fee'
}

export const REFUND_COMPONENT_LABELS = {
    [REFUND_COMPONENT_TYPES.PHOTOGRAPHER_FEE]: 'Photographer fee',
    [REFUND_COMPONENT_TYPES.PICA_FEE]: 'Pica fee',
    [REFUND_COMPONENT_TYPES.ADDITIONAL_FEE]: 'Additional fee',
    [REFUND_COMPONENT_TYPES.VAT]: 'VAT',
    [REFUND_COMPONENT_TYPES.STRIPE_FEE]: 'Stripe fee'
}

/**
 * Parse a component key to extract item ID and component type
 * @param {string} componentKey - e.g., "item_580_photographer_fee" or "order_stripe_fee"
 * @returns {{ scope: 'item'|'order', itemId: number|null, componentType: string }}
 */
export function parseComponentKey(componentKey) {
    if (componentKey.startsWith('item_')) {
        const parts = componentKey.split('_')
        const itemId = parseInt(parts[1])
        const componentType = parts.slice(2).join('_')
        return { scope: 'item', itemId, componentType }
    } else if (componentKey.startsWith('order_')) {
        const componentType = componentKey.replace('order_', '')
        return { scope: 'order', itemId: null, componentType }
    }
    return { scope: 'unknown', itemId: null, componentType: componentKey }
}

/**
 * Build a component key from parts
 * @param {'item'|'order'} scope - Component scope
 * @param {number|null} itemId - Item ID (required for item scope)
 * @param {string} componentType - Component type
 * @returns {string} Component key
 */
export function buildComponentKey(scope, itemId, componentType) {
    if (scope === 'item') {
        return `item_${itemId}_${componentType}`
    }
    return `order_${componentType}`
}

/**
 * Get the permission prefix based on scope type
 * @param {string} scopeType - 'global', 'contract-manager', or 'pica-service'
 * @returns {string} Permission prefix
 */
export function getPermissionPrefix(scopeType) {
    const prefixMap = {
        'global': 'admin',
        'contract-manager': 'contract_manager',
        'pica-service': 'pica_service'
    }
    return prefixMap[scopeType] || 'admin'
}

/**
 * Get the refund permission for a scope type
 * @param {string} scopeType - 'global', 'contract-manager', or 'pica-service'
 * @returns {string} Full permission string
 */
export function getRefundPermission(scopeType) {
    const prefix = getPermissionPrefix(scopeType)
    return `${prefix}.accounting.refund.create`
}

/**
 * Get the route prefix based on scope type
 * @param {string} scopeType - 'global', 'contract-manager', or 'pica-service'
 * @param {boolean} isEventContext - Whether we're in the event context (for pica-service)
 * @returns {string} Route prefix
 */
export function getRoutePrefix(scopeType, isEventContext = false) {
    if (scopeType === 'pica-service' && isEventContext) {
        return 'event.accounting'
    }
    const prefixMap = {
        'global': 'admin.accounting',
        'contract-manager': 'contractManager.accounting',
        'pica-service': 'event.accounting'  // Default to event context for pica-service
    }
    return prefixMap[scopeType] || 'admin.accounting'
}

/**
 * Get the movement route prefix based on scope type (CM/PS only)
 * @param {string} scopeType - 'contract-manager' or 'pica-service'
 * @returns {string} Route prefix
 */
export function getMovementRoutePrefix(scopeType) {
    const prefixMap = {
        'contract-manager': 'contractManager.movements',
        'pica-service': 'event.movements'
    }
    return prefixMap[scopeType] || 'event.movements'
}

/**
 * Get the detail route name for a transaction type within a scope (admin transactions)
 * @param {string} transactionType - Transaction type
 * @param {string} scopeType - Scope type
 * @returns {string} Route name
 */
export function getDetailRouteName(transactionType, scopeType = 'global') {
    const prefix = getRoutePrefix(scopeType)
    const routeMap = {
        [TRANSACTION_TYPES.ORDER]: `${prefix}.order`,
        [TRANSACTION_TYPES.ORDER_ITEM]: `${prefix}.order-item`,
        [TRANSACTION_TYPES.SERVICE_ORDER]: `${prefix}.service-order`,
        [TRANSACTION_TYPES.REFUND]: `${prefix}.refund`,
        [TRANSACTION_TYPES.PAYOUT]: `${prefix}.payout`
    }
    return routeMap[transactionType] || `${prefix}.order-item`
}

/**
 * Get the detail route params for navigating to a source (admin transactions)
 * @param {string} transactionType - Transaction type
 * @param {number} sourceId - Source ID
 * @param {Object} scope - Scope object { type, id }
 * @returns {Object} Route params { name, params }
 */
export function getDetailRouteParams(transactionType, sourceId, scope = { type: 'global' }) {
    // Payout is not available for pica-service
    if (transactionType === TRANSACTION_TYPES.PAYOUT && scope.type === 'pica-service') {
        return null
    }

    const routeName = getDetailRouteName(transactionType, scope.type)
    const params = { id: sourceId }

    // Add scope ID to params if needed
    if (scope.type === 'contract-manager' && scope.id) {
        params.contractManagerId = scope.id
    } else if (scope.type === 'pica-service' && scope.id) {
        params.picaServiceId = scope.id
    }

    return {
        name: routeName,
        params
    }
}

/**
 * Get the detail route name for a movement type (CM/PS movements)
 * @param {string} movementType - 'order_item' or 'refund'
 * @param {string} scopeType - 'contract-manager' or 'pica-service'
 * @returns {string} Route name
 */
export function getMovementDetailRouteName(movementType, scopeType) {
    const prefix = getMovementRoutePrefix(scopeType)
    const routeMap = {
        [MOVEMENT_TYPES.ORDER_ITEM]: `${prefix}.order-item`,
        [MOVEMENT_TYPES.REFUND]: `${prefix}.refund`
    }
    return routeMap[movementType] || `${prefix}.order-item`
}

/**
 * Get the detail route params for a movement (CM/PS)
 * @param {string} movementType - 'order_item' or 'refund'
 * @param {number} sourceId - Source ID
 * @param {Object} scope - Scope object { type, id }
 * @returns {Object} Route params { name, params }
 */
export function getMovementDetailRouteParams(movementType, sourceId, scope) {
    const routeName = getMovementDetailRouteName(movementType, scope.type)
    const params = { id: sourceId }

    if (scope.type === 'contract-manager' && scope.id) {
        params.contractManagerId = scope.id
    } else if (scope.type === 'pica-service' && scope.id) {
        params.picaServiceId = scope.id
    }

    return {
        name: routeName,
        params
    }
}
